import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Link } from "react-router";
import { MoreVertical } from "lucide-react";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "~/atoms/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { useCompanyGroups } from "~/api/swr/useCompanyGroups";
import { useSharedDocumentsOverview } from "~/api/v2/useSharedDocuments";
import { switchCompanyGroup, uploadCompanyGroupLogo } from "~/api/v2/requests";
import {
  type CompanyGroup,
  type SharedDocumentsOverviewRow,
} from "~/api/types";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import { cn } from "~/lib/utils";
import HeadingPage from "~/molecules/HeadingPage";
import { CompanyGroupLogo } from "~/molecules/CompanyGroupLogo";
import { CreateCompanyGroupModal } from "~/organisms/CreateCompanyGroupModal";
import { RenameCompanyGroupModal } from "~/organisms/RenameCompanyGroupModal";

const MAX_LOGO_SIZE_IN_BYTES = 1000000;

export const CompanyGroups = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUserContext();
  const { data: groups, isLoading, mutate } = useCompanyGroups();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number>();

  const isSuperadmin = user.role === PlatformRole.superAdmin;

  // This page is always viewed from the home group: a switched user is
  // reset before anything tenant-scoped renders, then reloaded so every
  // cached response is refetched in the home context.
  const homeGroup = groups?.find((group) => group.isHome);
  const isResetting = !!homeGroup && !homeGroup.isActive;

  useEffect(() => {
    if (!homeGroup || !isResetting) return;
    switchCompanyGroup(homeGroup.id).then(
      () => window.location.reload(),
      () => toast.error(t(($) => $.toasts.error.generic)),
    );
  }, [homeGroup, isResetting, t]);

  const rows = useMemo(() => {
    if (!groups) return [];
    const byId = new Map(groups.map((group) => [group.id, group]));
    const childrenOf = new Map<number, CompanyGroup[]>();
    const roots: CompanyGroup[] = [];
    for (const group of groups) {
      const parent = group.parentCompanyGroupId
        ? byId.get(group.parentCompanyGroupId)
        : undefined;
      if (parent) {
        childrenOf.set(parent.id, [
          ...(childrenOf.get(parent.id) ?? []),
          group,
        ]);
      } else {
        roots.push(group);
      }
    }
    // trail[i] = whether the guide line at indent level i continues below
    // this row; the last entry is the row's own connector (false = last
    // sibling, drawn as an elbow instead of a tee).
    const result: { group: CompanyGroup; trail: boolean[] }[] = [];
    const visit = (group: CompanyGroup, trail: boolean[]) => {
      result.push({ group, trail });
      const children = childrenOf.get(group.id) ?? [];
      children.forEach((child, index) =>
        visit(child, [...trail, index < children.length - 1]),
      );
    };
    roots.forEach((root) => visit(root, []));
    return result;
  }, [groups]);

  const selectedGroup =
    groups?.find((group) => group.id === selectedId) ??
    groups?.find((group) => group.isActive);

  return (
    <>
      <div className="space-y-4 px-6 py-7">
        <HeadingPage title={t(($) => $.companyGroups.header)} />

        {isLoading || !groups || isResetting ? (
          <CenterSpinner />
        ) : (
          <div className="flex flex-col gap-6 lg:flex-row">
            <aside className="w-full shrink-0 space-y-2 lg:w-96">
              {isSuperadmin && (
                <Button
                  className="w-full"
                  onClick={() => setShowCreateModal(true)}
                >
                  {t(($) => $.companyGroups.create.button)}
                </Button>
              )}
              <ul className="space-y-2">
                {rows.map(({ group, trail }) => (
                  <li key={group.id} className="flex">
                    {trail.map((continues, index) => {
                      const isConnector = index === trail.length - 1;
                      return (
                        <span key={index} className="relative w-8 shrink-0">
                          {(continues || isConnector) && (
                            <span
                              className={cn(
                                "bg-border absolute -top-2 left-4 w-px",
                                continues ? "-bottom-2" : "bottom-1/2",
                              )}
                            />
                          )}
                          {isConnector && (
                            <span className="bg-border absolute top-1/2 right-0 left-4 h-px" />
                          )}
                        </span>
                      );
                    })}
                    <div
                      className={cn(
                        "hover:bg-accent flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md border p-2",
                        group.isAncestor && "opacity-60",
                        selectedGroup?.id === group.id &&
                          "border-primary bg-accent",
                      )}
                      onClick={() => setSelectedId(group.id)}
                    >
                      <CompanyGroupLogo group={group} />
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span
                          lang={i18n.language}
                          title={group.name}
                          className="line-clamp-2 font-medium break-words hyphens-auto"
                        >
                          {group.name}
                        </span>
                        {group.isHome && (
                          <Badge>
                            {t(($) => $.companyGroups.yourOrganisation)}
                          </Badge>
                        )}
                      </div>
                      {/* A superadmin is never switched, so the active group is
                          home and the manageable set (active + descendants) is
                          exactly the non-ancestor groups. */}
                      {isSuperadmin && !group.isAncestor && (
                        <CompanyGroupActions
                          group={group}
                          onChanged={() => void mutate()}
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </aside>

            {selectedGroup && (
              <SharedDocumentsDetail
                key={selectedGroup.id}
                group={selectedGroup}
              />
            )}
          </div>
        )}
      </div>

      {groups && (
        <CreateCompanyGroupModal
          isOpen={showCreateModal}
          groups={groups}
          onSubmit={() => void mutate()}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </>
  );
};

const SharedDocumentsDetail = ({ group }: { group: CompanyGroup }) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  // The overview endpoint serves managers of the active group's subtree;
  // ancestors are display-only context in the tree.
  const canViewOverview =
    (user.role === PlatformRole.superAdmin ||
      user.role === PlatformRole.coordinator) &&
    !group.isAncestor;

  const { data: overview, isLoading } = useSharedDocumentsOverview(
    canViewOverview ? group.id : undefined,
  );

  return (
    <section className="min-w-0 flex-1 space-y-6">
      <h2 className="text-xl font-semibold">{group.name}</h2>

      {!canViewOverview ? null : isLoading || !overview ? (
        <CenterSpinner />
      ) : overview.sharedTo.length === 0 && overview.sharedBy.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(($) => $.sharedDocuments.noSharedDocuments)}
        </p>
      ) : (
        <>
          {overview.sharedTo.length > 0 && (
            <SharedDocumentsTable
              title={t(($) => $.sharedDocuments.sharedToGroup, {
                name: group.name,
              })}
              organisationLabel={t(($) => $.sharedDocuments.byOrganisation)}
              rows={overview.sharedTo}
              rowLink={
                group.isActive
                  ? (row) => `/shared-documents/${row.documentId}`
                  : undefined
              }
            />
          )}
          {overview.sharedBy.length > 0 && (
            <SharedDocumentsTable
              title={t(($) => $.sharedDocuments.sharedByGroup, {
                name: group.name,
              })}
              organisationLabel={t(($) => $.sharedDocuments.toOrganisation)}
              rows={overview.sharedBy}
              rowLink={
                group.isActive
                  ? (row) => `/documents/${row.documentId}`
                  : undefined
              }
            />
          )}
        </>
      )}
    </section>
  );
};

const SharedDocumentsTable = ({
  title,
  organisationLabel,
  rows,
  rowLink,
}: {
  title: string;
  organisationLabel: string;
  rows: SharedDocumentsOverviewRow[];
  rowLink?: (row: SharedDocumentsOverviewRow) => string;
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <h3 className="text-delegation font-medium">{title}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t(($) => $.sharedDocuments.document)}</TableHead>
            <TableHead>{organisationLabel}</TableHead>
            <TableHead>{t(($) => $.sharedDocuments.adopted)}</TableHead>
            <TableHead>{t(($) => $.sharedDocuments.readersOverview)}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.documentId}-${row.group.id}`}>
              <TableCell>
                {rowLink ? (
                  <Link to={rowLink(row)} className="hover:underline">
                    {row.title}
                  </Link>
                ) : (
                  row.title
                )}
              </TableCell>
              <TableCell>{row.group.name}</TableCell>
              <TableCell>
                {row.adoption ? (
                  <Badge variant="success">
                    {t(($) => $.sharedDocuments.adopted)}
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    {t(($) => $.sharedDocuments.notAdopted)}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {row.readers.readCount} / {row.readers.invitedCount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

interface CompanyGroupActionsProps {
  group: CompanyGroup;
  onChanged: () => void;
}

const CompanyGroupActions = ({
  group,
  onChanged,
}: CompanyGroupActionsProps) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);

  const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_IN_BYTES) {
      return toast.warning(t(($) => $.toasts.warning.maxSize));
    }
    setIsUploading(true);
    try {
      await uploadCompanyGroupLogo(group.id, file);
      onChanged();
      toast.success(t(($) => $.toasts.success.logoSaved));
    } catch {
      toast.error(t(($) => $.toasts.error.logoNotSaved));
    }
    setIsUploading(false);
  };

  return (
    <>
      <input
        ref={fileRef}
        onChange={onSelectFile}
        accept="image/png, image/jpeg, image/jpg"
        className="hidden"
        type="file"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={isUploading}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => setShowRenameModal(true)}>
            {t(($) => $.companyGroups.editName)}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => fileRef.current?.click()}>
            {group.logoUrl
              ? t(($) => $.companyGroups.replaceLogo)
              : t(($) => $.companyGroups.uploadLogo)}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <RenameCompanyGroupModal
        isOpen={showRenameModal}
        group={group}
        onSubmit={onChanged}
        onClose={() => setShowRenameModal(false)}
      />
    </>
  );
};
